/**
 * Every sentence Kontora says, in both languages it says them in.
 *
 * German is the source of truth: `MessageKey` is derived from it, so English is
 * a `Record<MessageKey, string>` and TypeScript refuses both a missing key and
 * an invented one. That is the whole parity mechanism — there is no separate
 * check to forget to run, and a half-translated screen does not compile.
 *
 * The two languages sit in one file on purpose. A new sentence is two adjacent
 * lines, and "did anybody translate this?" is answered by looking rather than by
 * diffing two files.
 *
 * Keys are `area.thing`. Placeholders are `{name}`, interpolated by `translate`.
 */

export const de = {
  // ---------------------------------------------------------------- chrome
  "app.name": "Kontora",
  "app.tagline": "Haushaltsbuch",
  "app.signOut": "Abmelden",
  "app.language": "Sprache",

  "nav.dashboard": "Übersicht",
  "nav.accounts": "Konten",
  "nav.transactions": "Buchungen",
  "nav.budgets": "Budgets",
  "nav.report": "Berichte",
  "nav.upcoming": "Wiederkehrend",
  "nav.goals": "Sparziele",
  "nav.import": "Import",
  "nav.categories": "Kategorien",

  // ------------------------------------------------------------------ shared
  "common.loading": "Wird geladen…",
  "common.save": "Speichern",
  "common.saving": "Wird gespeichert…",
  "common.cancel": "Abbrechen",
  "common.back": "Zurück",
  "common.optional": "optional",

  // ------------------------------------------------------------------- login
  "login.title": "Willkommen bei Kontora",
  "login.lead": "Deine Übersicht",
  "login.signIn": "Anmelden",

  // ------------------------------------------------------------------- money
  // What somebody is told when what they typed cannot be read as an amount.
  // These are keys rather than sentences because `money.ts` does integer
  // arithmetic and should not also be holding half the German interface.
  "money.empty": "Bitte einen Betrag eingeben",
  "money.noDigits": "„{text}“ enthält keine Ziffern",
  "money.manySeparators": "„{text}“ hat mehr als ein Dezimaltrennzeichen",
  "money.trailingSeparator": "„{text}“ endet mit einem Dezimaltrennzeichen",
  "money.tooManyDecimals": "„{text}“ hat {given} Nachkommastellen, {currency} hat {digits}",
  "money.notANumber": "„{text}“ ist keine Zahl",
  "money.tooLarge": "„{text}“ ist zu groß",

  // ------------------------------------------------------------- onboarding
  "onboarding.title": "Haushalt anlegen",
  "onboarding.lead":
    "Ein Haushalt ist der Rahmen für Konten, Buchungen und Budgets. Du kannst später weitere Personen einladen.",
  "onboarding.name": "Name",
  "onboarding.namePlaceholder": "Haushalt Weber",
  "onboarding.baseCurrency": "Basiswährung",
  "onboarding.baseCurrencyHint":
    "Alle Auswertungen rechnen in dieser Währung. Sie lässt sich später nicht ändern.",
  "onboarding.creating": "Wird angelegt…",

  "currency.EUR": "Euro (EUR)",
  "currency.CHF": "Schweizer Franken (CHF)",
  "currency.GBP": "Britisches Pfund (GBP)",
  "currency.USD": "US-Dollar (USD)",

  // ---------------------------------------------------------------- accounts
  "accounts.add": "Konto anlegen",
  "accounts.emptyTitle": "Noch kein Konto",
  "accounts.emptyBody": "Lege zuerst ein Konto an — danach kannst du Buchungen erfassen.",
  "accounts.opening": "Anfangssaldo",
  "accounts.movement": "Bewegung",

  "accounts.openingBalance": "Anfangssaldo",
  "account.name": "Name",
  "account.kind": "Art",
  "account.bank": "Bank",
  "account.openingHint": "Darf negativ sein — ein überzogenes Konto beginnt im Minus.",
  "account.openingDate": "Saldo gültig ab",
  "account.openingDateHint":
    "Vor diesem Datum ist der Saldo nicht definiert; frühere Buchungen zählen nicht mit.",

  "accountKind.checking": "Girokonto",
  "accountKind.savings": "Sparkonto",
  "accountKind.cash": "Bargeld",
  "accountKind.credit_card": "Kreditkarte",
  "accountKind.asset": "Vermögenswert",
  "accountKind.liability": "Verbindlichkeit",

  // ------------------------------------------------------------------ bars
  "budget.barLabel": "{label}: {spent} von {budget}",
  "budget.overspentBy": "{amount} über dem Budget",
  "budget.remaining": "noch {amount}",
  "goal.achieved": "erreicht",
  "goal.barLabel": "{label}: {saved} von {target}",
  "goal.overTarget": "{percent}% — {amount} über dem Ziel",
  "goal.remaining": "noch {amount}",
  "goal.pace": "{amount} im Monat bis dahin",

  "dashboard.cashflowMonthLabel": "{month}: {in} eingegangen, {out} ausgegeben",
  "dashboard.moneyIn": "Eingegangen",
  "dashboard.moneyOut": "Ausgegeben",

  // -------------------------------------------------------------- categories
  "common.create": "Anlegen",
  "categories.add": "Kategorie anlegen",
  "categories.namePlaceholder": "Lebensmittel",
  "categories.parent": "Übergeordnet",
  "categories.parentHint":
    "Zwei Ebenen sind das Maximum — eine Unterkategorie kann keine eigenen haben.",
  "categories.noParent": "Keine (oberste Ebene)",
  "categories.expense": "Ausgabe",
  "categories.income": "Einnahme",
  "categories.showArchived": "Archivierte anzeigen",
  "categories.emptyTitle": "Noch keine Kategorie",
  "categories.emptyBody":
    "Lege eine an, oder erfasse Buchungen zunächst ohne — „ohne Kategorie“ ist ein gültiger Zustand.",
  "categories.archived": "archiviert",
  "categories.archive": "Archivieren",
  "categories.unarchive": "Reaktivieren",

  // ------------------------------------------------------------ transactions
  "common.loadMore": "Weitere laden",
  "transactions.add": "Buchung erfassen",
  "transactions.transfer": "Umbuchung",
  "transactions.exporting": "Wird erstellt…",
  "transactions.filterByAccount": "Konto filtern",
  "transactions.allAccounts": "Alle Konten",
  "transactions.needAnAccount": "Buchungen brauchen ein Konto.",
  "transactions.emptyTitle": "Noch keine Buchung",
  "transactions.emptyBody": "Erfasse deine erste Buchung — Betrag, Datum, fertig.",
  "transactions.date": "Datum",
  "transactions.payee": "Empfänger",
  "transactions.account": "Konto",
  "transactions.amount": "Betrag",

  "allowance.usedUp":
    "Für diesen Monat sind die Buchungen des kostenlosen Tarifs aufgebraucht. Nächsten Monat geht es weiter.",
  "allowance.remaining": "Noch {n} Buchungen in diesem Monat im kostenlosen Tarif.",

  // ------------------------------------------------------- the entry form
  "transaction.save": "Buchung speichern",
  "transaction.direction": "Richtung",
  "transaction.amountHint": "Ohne Vorzeichen — die Richtung oben entscheidet.",
  "transaction.payeeHint": "Bekannte Empfänger werden vorgeschlagen.",
  "transaction.categoryPrefilled": "Kategorie „{category}“ aus der letzten Buchung übernommen.",
  "transaction.change": "Ändern",
  "transaction.category": "Kategorie",
  "transaction.categoryHint": "Kann auch später vergeben werden.",
  "transaction.withoutCategory": "Ohne Kategorie",
  "transaction.noAccount": "Kein Konto",
  "transaction.noCategory": "ohne Kategorie",
  "transaction.today": "heute",
  "transaction.note": "Notiz",
  "transaction.paidAbroad": "Im Ausland bezahlt",
  "transaction.paidAbroadHint":
    "Was auf dem Beleg stand. Der Kurs ergibt sich aus beiden Beträgen.",
  "transaction.receiptCurrency": "Währung des Belegs",
  "transaction.chooseAnAccount": "Bitte ein Konto wählen",
  "transaction.amountNotZero": "Der Betrag darf nicht null sein",
  "transaction.foreignAmountNotZero": "Der Fremdwährungsbetrag darf nicht null sein",

  // ------------------------------------------------------------- transfers
  "transfer.save": "Umbuchung speichern",
  "transfer.from": "Von",
  "transfer.to": "Nach",
  "transfer.amountHint": "Was das Quellkonto verlässt.",
  "transfer.arrived": "Angekommen",
  "transfer.arrivedHint":
    "Was gutgeschrieben wurde — die Differenz ist der Wechselkursaufschlag.",
  "transfer.needTwoTitle": "Zwei Konten werden gebraucht",
  "transfer.needTwoBody":
    "Eine Umbuchung verschiebt Geld zwischen zwei eigenen Konten — mit nur einem Konto gibt es nichts zu verschieben.",
  "transfer.chooseTwoAccounts": "Bitte zwei Konten wählen",

  // ------------------------------------------------------------- recurring
  "upcoming.rule": "Regel",
  "upcoming.add": "Regelmäßige Zahlung anlegen",
  "upcoming.namePlaceholder": "Fitnessstudio",
  "upcoming.incomePlaceholder": "Gehalt",
  "upcoming.repeatsEvery": "Wiederholt sich alle",
  "upcoming.unit": "Einheit",
  "upcoming.unit.day.one": "Tag",
  "upcoming.unit.day.many": "Tage",
  "upcoming.unit.week.one": "Woche",
  "upcoming.unit.week.many": "Wochen",
  "upcoming.unit.month.one": "Monat",
  "upcoming.unit.month.many": "Monate",
  "upcoming.unit.year.one": "Jahr",
  "upcoming.unit.year.many": "Jahre",
  "upcoming.countInvalid": "Bitte eine ganze Zahl ab 1",
  "upcoming.startDate": "Beginn",
  "upcoming.startDateHint": "Die erste Zahlung ist der erste passende Tag ab diesem Datum.",
  "upcoming.weekday": "Wochentag",
  "upcoming.dayOfMonth": "Tag im Monat",
  "upcoming.yearHint": "Der Monat ergibt sich aus dem Beginn.",
  "upcoming.every.day.one": "täglich",
  "upcoming.every.day.many": "alle {count} Tage",
  "upcoming.every.week.one": "wöchentlich am {weekday}",
  "upcoming.every.week.many": "alle {count} Wochen am {weekday}",
  "upcoming.every.month.one": "monatlich am {day}.",
  "upcoming.every.month.many": "alle {count} Monate am {day}.",
  "upcoming.every.year.one": "jährlich am {date}",
  "upcoming.every.year.many": "alle {count} Jahre am {date}",
  "upcoming.horizon": "Zeitraum",
  "upcoming.months": "{count} Monate",
  "upcoming.rules": "Regeln",
  "upcoming.until": "bis {date}",
  "upcoming.noEnd": "ohne Enddatum",
  "upcoming.next": "nächste am {date}",
  "upcoming.paused": "pausiert",
  "upcoming.edit": "Bearbeiten",
  "upcoming.editing": "„{name}“ bearbeiten",
  "upcoming.pause": "Pausieren",
  "upcoming.resume": "Fortsetzen",
  "upcoming.delete": "Löschen",
  "upcoming.deleteQuestion": "Wirklich löschen?",
  "upcoming.deleteConfirm": "Ja, löschen",
  "upcoming.deleteHint":
    "Künftige Termine verschwinden; bereits gebuchte und ausgesetzte Monate bleiben. Nur vorübergehend? Dann lieber pausieren.",
  "upcoming.accountFixed": "Das Konto legt die Währung fest und lässt sich nicht mehr ändern.",
  "upcoming.endDate": "Letzte Ausführung",
  "upcoming.endDateHint": "Leer lassen, wenn die Zahlung unbefristet läuft.",
  "upcoming.amountHint": "Ohne Vorzeichen — die Richtung oben entscheidet.",
  "upcoming.anchorHint": "Der 31. wird in kürzeren Monaten auf den Letzten gelegt.",
  "upcoming.emptyTitle": "Nichts steht an",
  "upcoming.emptyBody":
    "Trage unten ein, was regelmäßig abgeht oder eingeht — Miete, Versicherung, Abos, Gehalt. Kontora rechnet die Termine für die nächsten zwölf Monate aus.",
  "upcoming.needAnAccount": "Bitte zuerst ein Konto anlegen",
  "upcoming.fromStatement": "aus Kontoauszug",
  "upcoming.booked": "gebucht",
  "upcoming.skipped": "ausgesetzt",
  "upcoming.skip": "Aussetzen",
  "upcoming.unskip": "Doch zahlen",
  "upcoming.confirm": "Gebucht",
  "upcoming.bookings": "Anstehende Buchungen",
  "upcoming.autoBook": "Automatisch buchen",
  "upcoming.autoBookHint":
    "Am Fälligkeitstag bucht Kontora die Zahlung selbst, genau so, wie die Regel sie beschreibt. Einzelne Monate lassen sich vorher aussetzen.",
  "upcoming.autoBookTag": "bucht automatisch",
  "upcoming.autoBooked": "automatisch gebucht",
  "upcoming.overdue": "überfällig",
  "upcoming.totals": "Summen",
  "upcoming.totalsHint":
    "Laufende Regeln, auf Monat und Jahr umgerechnet. Pausierte und beendete Regeln zählen nicht mit.",
  "upcoming.monthlyIncome": "Monatliche Einnahmen",
  "upcoming.monthlyOutgoing": "Monatliche Ausgaben",
  "upcoming.yearlyOutgoing": "Jährliche Ausgaben",
  "upcoming.income": "Einnahmen",
  "upcoming.outgoing": "Ausgaben",
  "upcoming.byCategory": "Ausgaben nach Kategorie",
  "upcoming.perMonth": "pro Monat",
  "upcoming.perYear": "pro Jahr",
  "upcoming.checkHint": "Häkchen entfernen, um zu sehen, was die Summen ohne diese Zahlung wären.",
  "upcoming.include": "{name} einrechnen",
  "upcoming.counts": "Eingerechnet",
  "upcoming.includeAll": "Alle wieder einrechnen",
  "upcoming.saved": "Ersparnis: {monthly} im Monat, {yearly} im Jahr",

  // ----------------------------------------------------------------- goals
  "goals.add": "Sparziel anlegen",
  "goals.what": "Wofür",
  "goals.namePlaceholder": "Urlaub",
  "goals.target": "Ziel",
  "goals.targetHint": "Was am Ende zusammenkommen soll.",
  "goals.byWhen": "Bis wann",
  "goals.byWhenHint": "Mit einem Datum rechnet Kontora aus, was das im Monat bedeutet.",
  "goals.emptyTitle": "Noch kein Sparziel",
  "goals.emptyBody":
    "Urlaub, Notgroschen, ein neues Rad — trage unten ein, worauf gespart wird. Der Fortschritt ergibt sich aus dem, was eingezahlt wird.",
  "goals.payIn": "Einzahlen",
  "goals.payInFor": "Einzahlung für {name}",
  "goals.markAchieved": "Als erreicht markieren",
  "goals.unachieve": "Doch nicht erreicht",
  "goals.remove": "Ziel entfernen",
  "goals.targetNotZero": "Ein Ziel von null ist kein Ziel",
  "goals.contributionNotZero": "Null ist keine Einzahlung",

  // --------------------------------------------------------------- budgets
  "budgets.set": "Budget festlegen",
  "budgets.remove": "Budget entfernen",
  "budgets.previousMonth": "Vorheriger Monat",
  "budgets.nextMonth": "Nächster Monat",
  "budgets.chooseACategory": "Kategorie wählen",
  "budgets.amountHint": "Was in diesem Monat höchstens ausgegeben werden soll.",
  "budgets.notZero": "Ein Budget von null ist keine Grenze",
  "budgets.emptyTitle": "Noch kein Budget für diesen Monat",
  "budgets.emptyBody": "Lege eines an — die Vorschläge unten kommen aus den letzten Monaten.",
  "budgets.uncategorisedThisMonth": "Ohne Kategorie in diesem Monat:",
  "budgets.noBudgetCovers": "— kein Budget deckt das ab.",
  "budgets.suggestions": "Vorschläge",
  "budgets.suggestionsHint": "Aus den letzten Monaten berechnet — nichts davon ist gespeichert.",
  "budgets.typicalMonth": "Mittlerer Monat von {months}:",

  // --------------------------------------------------------------- reports
  "report.lead": "Ein ganzer Monat: was hereinkam, was hinausging, und wohin.",
  "report.stillRunning": "Dieser Monat läuft noch — gezählt bis {through}.",
  "report.moneyIn": "Eingenommen",
  "report.moneyOut": "Ausgegeben",
  "report.net": "Übrig",
  "report.betterThanBefore": "{delta} besser als {month}.",
  "report.worseThanBefore": "{delta} schlechter als {month}.",
  "report.sameAsBefore": "Genauso wie {month}.",
  "report.whereItWent": "Wohin das Geld ging",
  "report.whereItCameFrom": "Woher das Geld kam · {total}",
  "report.spentInTotal": "Ausgegeben",
  "report.receivedInTotal": "Eingenommen",
  "report.nothingSpent": "In diesem Monat wurde nichts ausgegeben.",
  "report.nothingReceived": "In diesem Monat kam nichts herein.",
  "report.uncategorised": "Ohne Kategorie",
  "report.otherCategories": "Sonstige",
  "report.ringLabel": "Aufteilung von {total} auf {n} Bereiche",
  "report.largestPayments": "Größte Ausgaben",
  "report.largestPaymentsLead": "Die {n} größten Zahlungen des Monats.",
  "report.dividedInto": "{n} Positionen",
  "report.noPayee": "Ohne Empfänger",

  // ------------------------------------------------------------- dashboard
  "dashboard.leftThisMonth": "Diesen Monat übrig",
  "dashboard.received": "eingegangen",
  "dashboard.spent": "ausgegeben",
  "dashboard.emptyMonth": "Für diesen Monat ist noch nichts erfasst",
  "dashboard.recordFirst": "Erste Buchung eintragen",
  "dashboard.inAndOut": "Ein- und Ausgaben",
  "dashboard.whereItWent": "Wohin es ging",
  "dashboard.nothingCategorised": "Noch nichts kategorisiert in diesem Monat.",
  "dashboard.netPosition": "Gesamtvermögen",
  "dashboard.netPositionUnavailable":
    "Lässt sich heute nicht bilden — für {pair} liegt kein Kurs vor.",
  "dashboard.acrossOneAccount": "über {n} Konto",
  "dashboard.acrossAccounts": "über {n} Konten",
  "dashboard.withConversion": "mit Umrechnung",
  "dashboard.recent": "Zuletzt gebucht",
  "dashboard.allTransactions": "Alle Buchungen",
  "dashboard.noneYet": "Noch keine Buchungen.",
  "dashboard.noPayee": "Ohne Empfänger",
  "dashboard.noComparison": "Kein Vergleich möglich — im Vormonat ist nichts erfasst.",
  "dashboard.more": "mehr",
  "dashboard.less": "weniger",
  "dashboard.same": "genauso viel",
  "dashboard.thanByLastMonth": "als am {date} im Vormonat",

  // ------------------------------------------------- statement field names
  "field.booked_date": "Buchungstag",
  "field.value_date": "Wertstellung",
  "field.amount": "Betrag",
  "field.debit": "Soll",
  "field.credit": "Haben",
  "field.sign": "Soll/Haben-Kennzeichen",
  "field.currency": "Währung",
  "field.payee": "Empfänger",
  "field.reference": "Referenz",
  "field.note": "Verwendungszweck",
  "field.bank_reference": "Bankreferenz",

  "outcome.imported": "gebucht",
  "outcome.skipped_duplicate": "übersprungen (Dublette)",
  "outcome.skipped_by_user": "ausgelassen",
  "outcome.failed": "Fehler",

  // ---------------------------------------------------------------- import
  "import.lead": "Kontora liest die Datei nur — gebucht wird nichts, bevor du es bestätigst.",
  "import.savedProfile": "Gespeichertes Profil",
  "import.newProfile": "Neues Profil",
  "import.deleteProfile": "Profil löschen",
  "import.statementFile": "Kontoauszug (CSV)",
  "import.chooseBank": "Bank wählen",
  "import.bankHint":
    "Wähle deine Bank, um Trennzeichen, Kodierung und Spalten vorzubelegen — die Vorschau zeigt danach, ob es passt.",
  "import.noFileTitle": "Noch keine Datei gewählt",
  "import.noFileBody":
    "Lade den CSV-Export deiner Bank hoch. Kontora erkennt Trennzeichen, Kodierung und Spalten und schlägt eine Zuordnung vor.",
  "import.mapColumns": "Spalten zuordnen",
  "import.detectedColumns": "{n} Spalten",
  "import.detectedDelimiter": "Trennzeichen",
  "import.detectedRows": "{n} Zeilen",
  "import.detectedHeaderRow": "{n} Kopfzeile",
  "import.detectedHeaderRows": "{n} Kopfzeilen",
  "import.doNotImport": "— nicht importieren —",
  "import.headerRows": "Kopfzeilen",
  "import.thousandsSeparator": "Tausendertrennzeichen",
  "import.separatorNone": "keines",
  "import.separatorDot": "Punkt",
  "import.separatorComma": "Komma",
  "import.separatorSpace": "Leerzeichen",
  "import.encoding": "Kodierung",
  "import.encodingAuto": "automatisch ({encoding})",
  "import.preview": "Vorschau",
  "import.rowsOf": "{shown} von {total} Zeilen",
  "import.rowsFailed": "{n} mit Fehler",
  "import.rowsDuplicate": "{n} schon vorhanden",
  "import.mapAtLeast": "Ordne mindestens Buchungstag und Betrag zu, um eine Vorschau zu sehen.",
  "import.row": "Zeile",
  "import.rowNumber": "Zeile {n}",
  "import.includeRow": "Zeile {n} importieren",
  "import.duplicateBank": "dieselbe Zahlung",
  "import.duplicateBankTitle": "Die Bank sagt: dieselbe Zahlung",
  "import.duplicateSoft": "schon vorhanden?",
  "import.duplicateSoftTitle": "Sieht aus wie eine Buchung, die es schon gibt",
  "import.commit": "Importieren",
  "import.chooseAnAccount":
    "Wähle unten ein Konto — Buchungen brauchen eins, und ohne Konto kann Kontora auch nicht sagen, was du schon hast.",
  "import.skipDuplicateOne": "{n} Zeile überspringen, die es schon gibt",
  "import.skipDuplicates": "{n} Zeilen überspringen, die es schon gibt",
  "import.importing": "Wird importiert…",
  "import.imported": "Importiert",
  "import.importNow": "Jetzt importieren",
  "import.bookedOf": "{imported} von {total} Zeilen gebucht",
  "import.duplicatesSkipped": "{n} Dubletten übersprungen",
  "import.leftOut": "{n} ausgelassen",
  "import.failed": "{n} fehlgeschlagen",
  "import.undo": "Import rückgängig machen",
  "import.saveMapping": "Zuordnung speichern",
  "import.saveMappingHint": "Beim nächsten Auszug dieser Bank ist alles schon eingestellt.",
  "import.profilePlaceholder": "Sparkasse Giro",
  "import.defaultAccount": "Standardkonto",
  "import.noDefault": "kein Standard",
  "import.saveProfile": "Profil speichern",
  "import.savedNote": "gespeichert",
  "import.pastImports": "Bisherige Importe",
  "import.wasUndone": "rückgängig gemacht",
  "import.restore": "Wiederherstellen",
  "import.revert": "Rückgängig",
  "import.savedProfiles": "Gespeicherte Profile",
  "import.neverUsed": "noch nicht verwendet",
  "import.timesUsed": "{n}× verwendet",
  "import.reset": "Zurücksetzen",

  "callback.failed": "Anmeldung fehlgeschlagen",
  "callback.backToSignIn": "Zurück zur Anmeldung",
  "callback.completing": "Anmeldung wird abgeschlossen…",

  // --------------------------------------------------------------- history
  "history.title": "Änderungen",
  "history.lead":
    "Wer was geändert hat. Was jemand selbst eingetippt hat — ein Verwendungszweck, ein Name — steht hier nicht: die Liste sagt, dass es sich geändert hat, nicht worauf.",
  "history.emptyTitle": "Noch nichts geändert",
  "history.emptyBody":
    "Sobald jemand etwas anlegt oder ändert, steht es hier — mit Namen und Datum.",
  "history.system": "Kontora",
  "history.fieldChanged": "{field} geändert",
  "history.action.create": "hat {thing} angelegt",
  "history.action.update": "hat {thing} geändert",
  "history.action.delete": "hat {thing} gelöscht",
  "history.action.restore": "hat {thing} wiederhergestellt",
  "history.entity.transaction": "eine Buchung",
  "history.entity.transfer": "eine Umbuchung",
  "history.entity.account": "ein Konto",
  "history.entity.budget": "ein Budget",
  "history.entity.category": "eine Kategorie",

  "auditField.amount_minor": "Betrag",
  "auditField.base_amount_minor": "Betrag in Basiswährung",
  "auditField.booked_date": "Buchungstag",
  "auditField.account_id": "Konto",
  "auditField.category_id": "Kategorie",
  "auditField.payee_id": "Empfänger",
  "auditField.payee_raw": "Empfänger",
  "auditField.note": "Notiz",
  "auditField.reference_text": "Verwendungszweck",
  "auditField.currency_code": "Währung",
  "auditField.is_cleared": "Bezahlt-Haken",
  "auditField.source": "Herkunft",
  "auditField.kind": "Art",
  "auditField.name": "Name",
  "auditField.institution_name": "Bank",
  "auditField.iban_last4": "IBAN-Endziffern",
  "auditField.is_archived": "Archiviert",
  "auditField.sort_order": "Reihenfolge",
  "auditField.opening_balance_minor": "Anfangssaldo",
  "auditField.opening_date": "Saldo gültig ab",
  "auditField.period_type": "Zeitraum",
  "auditField.period_start": "Zeitraumbeginn",
  "auditField.parent_id": "Übergeordnet",
  "auditField.depth": "Ebene",
  "auditField.original_amount_minor": "Betrag im Original",
  "auditField.original_currency_code": "Währung im Original",

  "dashboard.whoChangedWhat": "Wer was geändert hat",

  // ------------------------------------------------------------ your data
  "data.title": "Deine Daten",
  "data.lead":
    "Alles mitnehmen, oder alles löschen. Kontora bewahrt deine Daten auf, bis du sie löschst — nur der Originaltext importierter Kontoauszüge geht nach 90 Tagen.",
  "data.download": "Alles herunterladen",
  "data.downloadHint":
    "Eine Datei mit allem, was dieser Haushalt enthält: Konten, Buchungen, Kategorien, Budgets, Sparziele, Importe und die Änderungshistorie.",
  "data.downloadButton": "Daten herunterladen",
  "data.preparing": "Wird vorbereitet…",
  "data.eraseHousehold": "Haushalt löschen",
  "data.eraseHouseholdHint":
    "Löscht diesen Haushalt und alles darin — endgültig, für alle Mitglieder. Das lässt sich nicht rückgängig machen.",
  "data.typeTheName": "Zum Bestätigen „{name}“ eingeben",
  "data.eraseHouseholdButton": "Haushalt endgültig löschen",
  "data.erasing": "Wird gelöscht…",
  "data.erasePerson": "Konto löschen",
  "data.erasePersonHint":
    "Löscht deinen Namen und deine Adresse aus Kontora. Was du geändert hast, bleibt in der Historie stehen — ohne Namen.",
  "data.typeYourEmail": "Zum Bestätigen deine E-Mail-Adresse eingeben",
  "data.eraseHouseholdsToo":
    "Haushalte, in denen ich alleine bin, mitlöschen. Ohne dieses Häkchen bleiben sie stehen — und dann kommt niemand mehr an sie heran.",
  "data.erasePersonButton": "Konto endgültig löschen",
  "data.erasedTitle": "Dein Konto wurde gelöscht",

  "dashboard.yourData": "Deine Daten",

  // -------------------------------------------------------------- violations
  // Rejections the API sends as a code. The English sentence it sends alongside
  // is what shows when a code is not listed here, so this list is allowed to be
  // shorter than the API's — see `violationMessage`.
  "violation.required": "Pflichtfeld",
  "violation.end_date_invalid": "Die letzte Ausführung liegt vor dem Beginn der Regel",
  "violation.email": "Bitte eine E-Mail-Adresse eingeben",
  "violation.max": "Höchstens {param} Zeichen",
  "violation.min": "Mindestens {param}",
  "violation.len": "Genau {param} Zeichen",
  "violation.gte": "{param} oder mehr",
  "violation.lte": "{param} oder weniger",
  "violation.gt": "Mehr als {param}",
  "violation.lt": "Weniger als {param}",
  "violation.oneof": "Muss eines von: {param}",
  "violation.date": "Bitte ein Datum angeben, z. B. 2026-01-31",
  "violation.number": "Bitte eine Zahl angeben",
  "violation.positive_number": "Bitte eine positive Zahl angeben",
  "violation.unknown_currency": "Unbekannte Währung",
  "violation.unknown_category": "Unbekannte Kategorie",
  "violation.unknown_account": "Unbekanntes Konto",
  "violation.unknown_payee": "Unbekannter Zahlungsempfänger",
  "violation.unknown_transaction": "Unbekannte Buchung",
  "violation.duplicate_name": "Diesen Namen gibt es hier schon",
  "violation.category_archived": "Diese Kategorie ist archiviert",
  "violation.amount_not_positive": "Bitte einen Betrag größer als null angeben",
  "violation.amount_zero": "Eine Buchung über nichts ist keine Buchung",
  "violation.kind_invalid": "Bitte Einnahme oder Ausgabe wählen",
  "violation.kind_mismatch": "Eine Unterkategorie gehört in den Baum ihrer Elternkategorie",
  "violation.currency_immutable":
    "Die Währung eines Kontos lässt sich nicht ändern — lege ein neues Konto an",
  "violation.same_account": "Eine Umbuchung geht zwischen zwei verschiedenen Konten",
  "violation.splits_do_not_sum": "Die Aufteilungen ergeben zusammen nicht den Buchungsbetrag",
  "violation.category_or_splits":
    "Entweder eine Kategorie für die ganze Buchung oder Aufteilungen — nicht beides",
  "violation.file_empty": "Die Datei ist leer",
  "violation.file_required": "Bitte die Kontoauszugsdatei auswählen",
  "violation.format_unsupported": "Dieses Format kann Kontora noch nicht lesen",
  "violation.unsupported_locale": "Kontora gibt es noch nicht in dieser Sprache",
  "violation.period_month_only": "Budgets laufen über einen Monat",
  "violation.tolerance_range": "Die Toleranz liegt zwischen 0 und 10000 Basispunkten",

  // ------------------------------------------------------------ plan limits
  "plan.limit.accounts":
    "Der kostenlose Tarif umfasst ein Konto. Für mehr Konten brauchst du einen kostenpflichtigen Tarif.",
  "plan.limit.transactions":
    "Der kostenlose Tarif umfasst {allowed} Buchungen im Monat, und die sind für diesen Monat aufgebraucht. Nächsten Monat geht es weiter — oder du wechselst den Tarif.",
  "plan.limit.import":
    "Import gehört zu den kostenpflichtigen Tarifen. Buchungen von Hand erfassen geht immer.",
  "plan.limit.generic": "Dein Tarif erlaubt das nicht.",

  // ------------------------------------------------------ design rework
  "nav.history": "Änderungen",
  "nav.data": "Deine Daten",
  "nav.manual": "Handbuch",
  "nav.group.overview": "Überblick",
  "nav.group.money": "Geld",
  "nav.group.plan": "Planen",
  "nav.group.manage": "Verwalten",
  "nav.main": "Hauptnavigation",
  "app.settings": "Einstellungen",
  "app.openSettings": "Einstellungen öffnen",
  "app.closeSettings": "Einstellungen schließen",
  "app.signedInAs": "Angemeldet als {who}",
  "scheme.label": "Darstellung",
  "scheme.system": "System",
  "scheme.light": "Hell",
  "scheme.dark": "Dunkel",
  "theme.label": "Farbschema",
  "theme.hint": "Mit „System“ folgt die Darstellung deinem Gerät.",
  "theme.kontor": "Kontor",
  "theme.kontor.note": "Tintenblau",
  "theme.tanne": "Tanne",
  "theme.tanne.note": "Tannengrün",
  "theme.pflaume": "Pflaume",
  "theme.pflaume.note": "Pflaumenviolett",
  "theme.bernstein": "Bernstein",
  "theme.bernstein.note": "Warmer Bernstein",
  "theme.graphit": "Graphit",
  "theme.graphit.note": "Schwarz-weiß",
  // -- design: overview (keys added by the overview rework go directly below this line)
  // -- design: entry (keys added by the entry rework go directly below this line)
  // -- design: planning (keys added by the planning rework go directly below this line)
  "budgets.add": "Budget festlegen",
  // -- design: admin (keys added by the admin rework go directly below this line)
  "login.example.salary": "Gehalt",
  "login.example.rent": "Miete",
  "login.example.groceries": "Lebensmittel",
  "login.example.balance": "Saldo",
  "data.dangerZoneTitle": "Löschen",
  "data.dangerZoneHint":
    "Beide Schritte sind endgültig. Lade deine Daten herunter, bevor du hier fortfährst.",
  "import.step1Title": "Datei & Bank",

  // --------------------------------------------------------------- manual
  "manual.contents": "Inhalt",
  "manual.notFound": "Dieses Kapitel gibt es nicht",
  "manual.backToIndex": "Zurück zur Übersicht",
  "manual.loadError": "Das Handbuch konnte nicht geladen werden",
} as const;

export type MessageKey = keyof typeof de;

/** True when the catalogue has a key by this name. */
export function hasMessage(key: string): key is MessageKey {
  return key in de;
}

export const en: Record<MessageKey, string> = {
  // ---------------------------------------------------------------- chrome
  "app.name": "Kontora",
  "app.tagline": "Household finances",
  "app.signOut": "Sign out",
  "app.language": "Language",

  "nav.dashboard": "Overview",
  "nav.accounts": "Accounts",
  "nav.transactions": "Transactions",
  "nav.budgets": "Budgets",
  "nav.report": "Reports",
  "nav.upcoming": "Recurring",
  "nav.goals": "Goals",
  "nav.import": "Import",
  "nav.categories": "Categories",

  // ------------------------------------------------------------------ shared
  "common.loading": "Loading…",
  "common.save": "Save",
  "common.saving": "Saving…",
  "common.cancel": "Cancel",
  "common.back": "Back",
  "common.optional": "optional",

  // ------------------------------------------------------------------- login
  "login.title": "Welcome to Kontora",
  "login.lead": "Keep track of your money — for the household and the side business.",
  "login.signIn": "Sign in",

  // ------------------------------------------------------------------- money
  "money.empty": "Please enter an amount",
  "money.noDigits": "“{text}” has no digits in it",
  "money.manySeparators": "“{text}” has more than one decimal separator",
  "money.trailingSeparator": "“{text}” ends with a decimal separator",
  "money.tooManyDecimals": "“{text}” has {given} decimal places, {currency} has {digits}",
  "money.notANumber": "“{text}” is not a number",
  "money.tooLarge": "“{text}” is too large",

  // ------------------------------------------------------------- onboarding
  "onboarding.title": "Create a household",
  "onboarding.lead":
    "A household is the frame around accounts, transactions and budgets. You can invite other people later.",
  "onboarding.name": "Name",
  "onboarding.namePlaceholder": "Weber household",
  "onboarding.baseCurrency": "Base currency",
  "onboarding.baseCurrencyHint":
    "Every report is calculated in this currency. It cannot be changed later.",
  "onboarding.creating": "Creating…",

  "currency.EUR": "Euro (EUR)",
  "currency.CHF": "Swiss franc (CHF)",
  "currency.GBP": "Pound sterling (GBP)",
  "currency.USD": "US dollar (USD)",

  // ---------------------------------------------------------------- accounts
  "accounts.add": "Add an account",
  "accounts.emptyTitle": "No account yet",
  "accounts.emptyBody": "Add an account first — then you can record transactions.",
  "accounts.opening": "opening",
  "accounts.movement": "movement",

  "accounts.openingBalance": "Opening balance",
  "account.name": "Name",
  "account.kind": "Kind",
  "account.bank": "Bank",
  "account.openingHint": "May be negative — an overdrawn account starts below zero.",
  "account.openingDate": "Balance valid from",
  "account.openingDateHint":
    "Before this date the balance is undefined; earlier transactions do not count towards it.",

  "accountKind.checking": "Current account",
  "accountKind.savings": "Savings account",
  "accountKind.cash": "Cash",
  "accountKind.credit_card": "Credit card",
  "accountKind.asset": "Asset",
  "accountKind.liability": "Liability",

  // ------------------------------------------------------------------ bars
  "budget.barLabel": "{label}: {spent} of {budget}",
  "budget.overspentBy": "{amount} over budget",
  "budget.remaining": "{amount} left",
  "goal.achieved": "reached",
  "goal.barLabel": "{label}: {saved} of {target}",
  "goal.overTarget": "{percent}% — {amount} past the target",
  "goal.remaining": "{amount} to go",
  "goal.pace": "{amount} a month to get there",

  "dashboard.cashflowMonthLabel": "{month}: {in} in, {out} out",
  "dashboard.moneyIn": "In",
  "dashboard.moneyOut": "Out",

  // -------------------------------------------------------------- categories
  "common.create": "Create",
  "categories.add": "Add a category",
  "categories.namePlaceholder": "Groceries",
  "categories.parent": "Parent",
  "categories.parentHint":
    "Two levels is the maximum — a subcategory cannot have subcategories of its own.",
  "categories.noParent": "None (top level)",
  "categories.expense": "Expense",
  "categories.income": "Income",
  "categories.showArchived": "Show archived",
  "categories.emptyTitle": "No categories yet",
  "categories.emptyBody":
    "Add one, or record transactions without for now — “no category” is a valid state.",
  "categories.archived": "archived",
  "categories.archive": "Archive",
  "categories.unarchive": "Reactivate",

  // ------------------------------------------------------------ transactions
  "common.loadMore": "Load more",
  "transactions.add": "Add a transaction",
  "transactions.transfer": "Transfer",
  "transactions.exporting": "Preparing…",
  "transactions.filterByAccount": "Filter by account",
  "transactions.allAccounts": "All accounts",
  "transactions.needAnAccount": "Transactions need an account.",
  "transactions.emptyTitle": "No transactions yet",
  "transactions.emptyBody": "Record your first one — amount, date, done.",
  "transactions.date": "Date",
  "transactions.payee": "Payee",
  "transactions.account": "Account",
  "transactions.amount": "Amount",

  "allowance.usedUp":
    "This month's transactions on the free plan are used up. Next month starts fresh.",
  "allowance.remaining": "{n} more transactions this month on the free plan.",

  // ------------------------------------------------------- the entry form
  "transaction.save": "Save transaction",
  "transaction.direction": "Direction",
  "transaction.amountHint": "No sign — the direction above decides.",
  "transaction.payeeHint": "Payees you have used before are suggested.",
  "transaction.categoryPrefilled": "Category “{category}” carried over from the last one.",
  "transaction.change": "Change",
  "transaction.category": "Category",
  "transaction.categoryHint": "Can be filled in later.",
  "transaction.withoutCategory": "No category",
  "transaction.noAccount": "No account",
  "transaction.noCategory": "no category",
  "transaction.today": "today",
  "transaction.note": "Note",
  "transaction.paidAbroad": "Paid abroad",
  "transaction.paidAbroadHint":
    "What the receipt said. The rate follows from the two amounts.",
  "transaction.receiptCurrency": "Currency on the receipt",
  "transaction.chooseAnAccount": "Please choose an account",
  "transaction.amountNotZero": "The amount cannot be zero",
  "transaction.foreignAmountNotZero": "The foreign amount cannot be zero",

  // ------------------------------------------------------------- transfers
  "transfer.save": "Save transfer",
  "transfer.from": "From",
  "transfer.to": "To",
  "transfer.amountHint": "What leaves the source account.",
  "transfer.arrived": "Arrived",
  "transfer.arrivedHint":
    "What was credited — the difference is the exchange spread.",
  "transfer.needTwoTitle": "Two accounts are needed",
  "transfer.needTwoBody":
    "A transfer moves money between two of your own accounts — with only one there is nothing to move.",
  "transfer.chooseTwoAccounts": "Please choose two accounts",

  // ------------------------------------------------------------- recurring
  "upcoming.rule": "Rule",
  "upcoming.add": "Add a recurring payment",
  "upcoming.namePlaceholder": "Gym",
  "upcoming.incomePlaceholder": "Salary",
  "upcoming.repeatsEvery": "Repeats every",
  "upcoming.unit": "Unit",
  "upcoming.unit.day.one": "day",
  "upcoming.unit.day.many": "days",
  "upcoming.unit.week.one": "week",
  "upcoming.unit.week.many": "weeks",
  "upcoming.unit.month.one": "month",
  "upcoming.unit.month.many": "months",
  "upcoming.unit.year.one": "year",
  "upcoming.unit.year.many": "years",
  "upcoming.countInvalid": "Enter a whole number, 1 or more",
  "upcoming.startDate": "Starts",
  "upcoming.startDateHint": "The first payment is the first matching day on or after this date.",
  "upcoming.weekday": "Weekday",
  "upcoming.dayOfMonth": "Day of the month",
  "upcoming.yearHint": "The month comes from the start date.",
  "upcoming.every.day.one": "daily",
  "upcoming.every.day.many": "every {count} days",
  "upcoming.every.week.one": "weekly on {weekday}",
  "upcoming.every.week.many": "every {count} weeks on {weekday}",
  "upcoming.every.month.one": "monthly on the {day}.",
  "upcoming.every.month.many": "every {count} months on the {day}.",
  "upcoming.every.year.one": "yearly on {date}",
  "upcoming.every.year.many": "every {count} years on {date}",
  "upcoming.horizon": "Period",
  "upcoming.months": "{count} months",
  "upcoming.rules": "Rules",
  "upcoming.until": "until {date}",
  "upcoming.noEnd": "no end date",
  "upcoming.next": "next on {date}",
  "upcoming.paused": "paused",
  "upcoming.edit": "Edit",
  "upcoming.editing": "Edit “{name}”",
  "upcoming.pause": "Pause",
  "upcoming.resume": "Resume",
  "upcoming.delete": "Delete",
  "upcoming.deleteQuestion": "Delete for good?",
  "upcoming.deleteConfirm": "Yes, delete",
  "upcoming.deleteHint":
    "Future dates disappear; months already booked or skipped stay. Only for a while? Pause it instead.",
  "upcoming.accountFixed": "The account sets the currency and cannot be changed.",
  "upcoming.endDate": "Last payment",
  "upcoming.endDateHint": "Leave empty if the payment runs indefinitely.",
  "upcoming.amountHint": "No sign — the direction above decides.",
  "upcoming.anchorHint": "The 31st falls on the last day in shorter months.",
  "upcoming.emptyTitle": "Nothing due",
  "upcoming.emptyBody":
    "Add what goes out or comes in regularly — rent, insurance, subscriptions, salary. Kontora works out the dates for the next twelve months.",
  "upcoming.needAnAccount": "Add an account first",
  "upcoming.fromStatement": "from a statement",
  "upcoming.booked": "booked",
  "upcoming.skipped": "skipped",
  "upcoming.skip": "Skip",
  "upcoming.unskip": "Pay after all",
  "upcoming.confirm": "Booked",
  "upcoming.bookings": "Upcoming bookings",
  "upcoming.autoBook": "Book automatically",
  "upcoming.autoBookHint":
    "On the due date Kontora books the payment itself, exactly as the rule describes it. You can skip single months beforehand.",
  "upcoming.autoBookTag": "books automatically",
  "upcoming.autoBooked": "booked automatically",
  "upcoming.overdue": "overdue",
  "upcoming.totals": "Totals",
  "upcoming.totalsHint":
    "Running rules, converted to a month and a year. Paused and ended rules are left out.",
  "upcoming.monthlyIncome": "Monthly income",
  "upcoming.monthlyOutgoing": "Monthly outgoing",
  "upcoming.yearlyOutgoing": "Yearly outgoing",
  "upcoming.income": "Income",
  "upcoming.outgoing": "Outgoing",
  "upcoming.byCategory": "Outgoing by category",
  "upcoming.perMonth": "per month",
  "upcoming.perYear": "per year",
  "upcoming.checkHint": "Untick a payment to see what the totals would be without it.",
  "upcoming.include": "Include {name}",
  "upcoming.counts": "Included",
  "upcoming.includeAll": "Include all again",
  "upcoming.saved": "Saved: {monthly} a month, {yearly} a year",

  // ----------------------------------------------------------------- goals
  "goals.add": "Add a savings goal",
  "goals.what": "What for",
  "goals.namePlaceholder": "Holiday",
  "goals.target": "Target",
  "goals.targetHint": "What should be saved up in the end.",
  "goals.byWhen": "By when",
  "goals.byWhenHint": "With a date, Kontora works out what that means per month.",
  "goals.emptyTitle": "No savings goal yet",
  "goals.emptyBody":
    "A holiday, a rainy-day fund, a new bike — add what you are saving for below. The progress follows from what gets paid in.",
  "goals.payIn": "Pay in",
  "goals.payInFor": "Pay in towards {name}",
  "goals.markAchieved": "Mark as reached",
  "goals.unachieve": "Not reached after all",
  "goals.remove": "Remove goal",
  "goals.targetNotZero": "A target of nothing is not a target",
  "goals.contributionNotZero": "Nothing is not a contribution",

  // --------------------------------------------------------------- budgets
  "budgets.set": "Set budget",
  "budgets.remove": "Remove budget",
  "budgets.previousMonth": "Previous month",
  "budgets.nextMonth": "Next month",
  "budgets.chooseACategory": "Choose a category",
  "budgets.amountHint": "The most that should be spent this month.",
  "budgets.notZero": "A budget of nothing is not a limit",
  "budgets.emptyTitle": "No budget for this month yet",
  "budgets.emptyBody": "Set one — the suggestions below come from the last few months.",
  "budgets.uncategorisedThisMonth": "Without a category this month:",
  "budgets.noBudgetCovers": "— no budget covers it.",
  "budgets.suggestions": "Suggestions",
  "budgets.suggestionsHint": "Worked out from the last few months — none of it is stored.",
  "budgets.typicalMonth": "Typical month of {months}:",

  // --------------------------------------------------------------- reports
  "report.lead": "A whole month: what came in, what went out, and where it went.",
  "report.stillRunning": "This month is still running — counted to {through}.",
  "report.moneyIn": "Received",
  "report.moneyOut": "Spent",
  "report.net": "Left",
  "report.betterThanBefore": "{delta} better than {month}.",
  "report.worseThanBefore": "{delta} worse than {month}.",
  "report.sameAsBefore": "The same as {month}.",
  "report.whereItWent": "Where the money went",
  "report.whereItCameFrom": "Where the money came from · {total}",
  "report.spentInTotal": "Spent",
  "report.receivedInTotal": "Received",
  "report.nothingSpent": "Nothing was spent this month.",
  "report.nothingReceived": "Nothing came in this month.",
  "report.uncategorised": "Uncategorised",
  "report.otherCategories": "Other",
  "report.ringLabel": "{total} split across {n} areas",
  "report.largestPayments": "Largest payments",
  "report.largestPaymentsLead": "The {n} biggest payments of the month.",
  "report.dividedInto": "{n} items",
  "report.noPayee": "No payee",

  // ------------------------------------------------------------- dashboard
  "dashboard.leftThisMonth": "Left this month",
  "dashboard.received": "in",
  "dashboard.spent": "out",
  "dashboard.emptyMonth": "Nothing recorded for this month yet",
  "dashboard.recordFirst": "Record the first transaction",
  "dashboard.inAndOut": "Money in and out",
  "dashboard.whereItWent": "Where it went",
  "dashboard.nothingCategorised": "Nothing categorised this month yet.",
  "dashboard.netPosition": "Net position",
  "dashboard.netPositionUnavailable":
    "Cannot be worked out today — there is no rate for {pair}.",
  "dashboard.acrossOneAccount": "across {n} account",
  "dashboard.acrossAccounts": "across {n} accounts",
  "dashboard.withConversion": "with conversion",
  "dashboard.recent": "Recently booked",
  "dashboard.allTransactions": "All transactions",
  "dashboard.noneYet": "No transactions yet.",
  "dashboard.noPayee": "No payee",
  "dashboard.noComparison": "No comparison — nothing was recorded last month.",
  "dashboard.more": "more",
  "dashboard.less": "less",
  "dashboard.same": "the same",
  "dashboard.thanByLastMonth": "than by {date} last month",

  // ------------------------------------------------- statement field names
  "field.booked_date": "Booking date",
  "field.value_date": "Value date",
  "field.amount": "Amount",
  "field.debit": "Debit",
  "field.credit": "Credit",
  "field.sign": "Debit/credit marker",
  "field.currency": "Currency",
  "field.payee": "Payee",
  "field.reference": "Reference",
  "field.note": "Description",
  "field.bank_reference": "Bank reference",

  "outcome.imported": "booked",
  "outcome.skipped_duplicate": "skipped (duplicate)",
  "outcome.skipped_by_user": "left out",
  "outcome.failed": "Error",

  // ---------------------------------------------------------------- import
  "import.lead": "Kontora only reads the file — nothing is booked until you confirm it.",
  "import.savedProfile": "Saved profile",
  "import.newProfile": "New profile",
  "import.deleteProfile": "Delete profile",
  "import.statementFile": "Bank statement (CSV)",
  "import.chooseBank": "Choose your bank",
  "import.bankHint":
    "Pick your bank to pre-fill the separator, encoding and columns — the preview then shows whether it fits.",
  "import.noFileTitle": "No file chosen yet",
  "import.noFileBody":
    "Upload your bank's CSV export. Kontora works out the separator, the encoding and the columns, and proposes a mapping.",
  "import.mapColumns": "Map the columns",
  "import.detectedColumns": "{n} columns",
  "import.detectedDelimiter": "separator",
  "import.detectedRows": "{n} rows",
  "import.detectedHeaderRow": "{n} header row",
  "import.detectedHeaderRows": "{n} header rows",
  "import.doNotImport": "— do not import —",
  "import.headerRows": "Header rows",
  "import.thousandsSeparator": "Thousands separator",
  "import.separatorNone": "none",
  "import.separatorDot": "Dot",
  "import.separatorComma": "Comma",
  "import.separatorSpace": "Space",
  "import.encoding": "Encoding",
  "import.encodingAuto": "automatic ({encoding})",
  "import.preview": "Preview",
  "import.rowsOf": "{shown} of {total} rows",
  "import.rowsFailed": "{n} with an error",
  "import.rowsDuplicate": "{n} already here",
  "import.mapAtLeast": "Map at least the booking date and the amount to see a preview.",
  "import.row": "Row",
  "import.rowNumber": "Row {n}",
  "import.includeRow": "Import row {n}",
  "import.duplicateBank": "same payment",
  "import.duplicateBankTitle": "The bank says: the same payment",
  "import.duplicateSoftTitle": "Looks like a transaction you already have",
  "import.duplicateSoft": "already here?",
  "import.commit": "Import",
  "import.chooseAnAccount":
    "Choose an account below — transactions need one, and without it Kontora cannot tell what you already have.",
  "import.skipDuplicateOne": "Skip {n} row that is already here",
  "import.skipDuplicates": "Skip {n} rows that are already here",
  "import.importing": "Importing…",
  "import.imported": "Imported",
  "import.importNow": "Import now",
  "import.bookedOf": "{imported} of {total} rows booked",
  "import.duplicatesSkipped": "{n} duplicates skipped",
  "import.leftOut": "{n} left out",
  "import.failed": "{n} failed",
  "import.undo": "Undo this import",
  "import.saveMapping": "Save this mapping",
  "import.saveMappingHint": "Next time this bank sends a statement, it is already set up.",
  "import.profilePlaceholder": "Sparkasse current account",
  "import.defaultAccount": "Default account",
  "import.noDefault": "no default",
  "import.saveProfile": "Save profile",
  "import.savedNote": "saved",
  "import.pastImports": "Past imports",
  "import.wasUndone": "undone",
  "import.restore": "Restore",
  "import.revert": "Undo",
  "import.savedProfiles": "Saved profiles",
  "import.neverUsed": "not used yet",
  "import.timesUsed": "used {n}×",
  "import.reset": "Reset",

  "callback.failed": "Sign-in failed",
  "callback.backToSignIn": "Back to sign-in",
  "callback.completing": "Completing sign-in…",

  // --------------------------------------------------------------- history
  "history.title": "Changes",
  "history.lead":
    "Who changed what. What somebody typed themselves — a description, a name — is not here: the list says that it changed, not what to.",
  "history.emptyTitle": "Nothing changed yet",
  "history.emptyBody":
    "As soon as somebody adds or changes something it appears here, with a name and a date.",
  "history.system": "Kontora",
  "history.fieldChanged": "{field} changed",
  "history.action.create": "added {thing}",
  "history.action.update": "changed {thing}",
  "history.action.delete": "deleted {thing}",
  "history.action.restore": "restored {thing}",
  "history.entity.transaction": "a transaction",
  "history.entity.transfer": "a transfer",
  "history.entity.account": "an account",
  "history.entity.budget": "a budget",
  "history.entity.category": "a category",

  "auditField.amount_minor": "Amount",
  "auditField.base_amount_minor": "Amount in base currency",
  "auditField.booked_date": "Booking date",
  "auditField.account_id": "Account",
  "auditField.category_id": "Category",
  "auditField.payee_id": "Payee",
  "auditField.payee_raw": "Payee",
  "auditField.note": "Note",
  "auditField.reference_text": "Description",
  "auditField.currency_code": "Currency",
  "auditField.is_cleared": "Cleared",
  "auditField.source": "Source",
  "auditField.kind": "Kind",
  "auditField.name": "Name",
  "auditField.institution_name": "Bank",
  "auditField.iban_last4": "IBAN last four",
  "auditField.is_archived": "Archived",
  "auditField.sort_order": "Order",
  "auditField.opening_balance_minor": "Opening balance",
  "auditField.opening_date": "Balance valid from",
  "auditField.period_type": "Period",
  "auditField.period_start": "Period start",
  "auditField.parent_id": "Parent",
  "auditField.depth": "Level",
  "auditField.original_amount_minor": "Original amount",
  "auditField.original_currency_code": "Original currency",

  "dashboard.whoChangedWhat": "Who changed what",

  // ------------------------------------------------------------ your data
  "data.title": "Your data",
  "data.lead":
    "Take it with you, or have it deleted. Kontora keeps your data until you delete it — only the original text of imported statements goes, after 90 days.",
  "data.download": "Download everything",
  "data.downloadHint":
    "One file with everything this household holds: accounts, transactions, categories, budgets, savings goals, imports and the change history.",
  "data.downloadButton": "Download my data",
  "data.preparing": "Preparing…",
  "data.eraseHousehold": "Delete this household",
  "data.eraseHouseholdHint":
    "Deletes this household and everything in it — permanently, for every member. This cannot be undone.",
  "data.typeTheName": "Type “{name}” to confirm",
  "data.eraseHouseholdButton": "Delete this household for good",
  "data.erasing": "Deleting…",
  "data.erasePerson": "Delete your account",
  "data.erasePersonHint":
    "Removes your name and address from Kontora. What you changed stays in the history — without a name on it.",
  "data.typeYourEmail": "Type your email address to confirm",
  "data.eraseHouseholdsToo":
    "Delete any household I am the only member of. Without this they stay, and then nobody can reach them.",
  "data.erasePersonButton": "Delete my account for good",
  "data.erasedTitle": "Your account has been deleted",

  "dashboard.yourData": "Your data",

  // -------------------------------------------------------------- violations
  "violation.required": "Required",
  "violation.end_date_invalid": "The last payment is before the rule starts",
  "violation.email": "Please enter an email address",
  "violation.max": "At most {param} characters",
  "violation.min": "At least {param}",
  "violation.len": "Exactly {param} characters",
  "violation.gte": "{param} or more",
  "violation.lte": "{param} or less",
  "violation.gt": "More than {param}",
  "violation.lt": "Less than {param}",
  "violation.oneof": "Must be one of: {param}",
  "violation.date": "Please give a date, e.g. 2026-01-31",
  "violation.number": "Please give a number",
  "violation.positive_number": "Please give a positive number",
  "violation.unknown_currency": "Unknown currency",
  "violation.unknown_category": "Unknown category",
  "violation.unknown_account": "Unknown account",
  "violation.unknown_payee": "Unknown payee",
  "violation.unknown_transaction": "Unknown transaction",
  "violation.duplicate_name": "That name is already taken here",
  "violation.category_archived": "This category is archived",
  "violation.amount_not_positive": "Please give an amount greater than zero",
  "violation.amount_zero": "A booking of nothing is not a booking",
  "violation.kind_invalid": "Please choose income or expense",
  "violation.kind_mismatch": "A subcategory belongs to its parent's tree",
  "violation.currency_immutable":
    "An account's currency cannot be changed — create a new account instead",
  "violation.same_account": "A transfer moves money between two different accounts",
  "violation.splits_do_not_sum": "The splits do not add up to the transaction amount",
  "violation.category_or_splits":
    "Either one category for the whole transaction or a set of splits — not both",
  "violation.file_empty": "The file is empty",
  "violation.file_required": "Please choose the statement file",
  "violation.format_unsupported": "Kontora cannot read this format yet",
  "violation.unsupported_locale": "Kontora is not available in this language yet",
  "violation.period_month_only": "Budgets run for a month",
  "violation.tolerance_range": "The tolerance is between 0 and 10000 basis points",

  // ------------------------------------------------------------ plan limits
  "plan.limit.accounts":
    "The free plan includes one account. More accounts need a paid plan.",
  "plan.limit.transactions":
    "The free plan includes {allowed} bookings a month, and this month's are used up. Next month starts fresh — or change plan.",
  "plan.limit.import":
    "Importing statements is part of the paid plans. Recording bookings by hand is always included.",
  "plan.limit.generic": "Your plan does not allow this.",

  // ------------------------------------------------------ design rework
  "nav.history": "Changes",
  "nav.data": "Your data",
  "nav.manual": "Manual",
  "nav.group.overview": "Overview",
  "nav.group.money": "Money",
  "nav.group.plan": "Plan",
  "nav.group.manage": "Manage",
  "nav.main": "Main navigation",
  "app.settings": "Settings",
  "app.openSettings": "Open settings",
  "app.closeSettings": "Close settings",
  "app.signedInAs": "Signed in as {who}",
  "scheme.label": "Appearance",
  "scheme.system": "System",
  "scheme.light": "Light",
  "scheme.dark": "Dark",
  "theme.label": "Theme",
  "theme.hint": "With “System”, the appearance follows your device.",
  "theme.kontor": "Kontor",
  "theme.kontor.note": "Ink blue",
  "theme.tanne": "Tanne",
  "theme.tanne.note": "Fir green",
  "theme.pflaume": "Pflaume",
  "theme.pflaume.note": "Plum",
  "theme.bernstein": "Bernstein",
  "theme.bernstein.note": "Warm amber",
  "theme.graphit": "Graphit",
  "theme.graphit.note": "Black and white",
  // -- design: overview (keys added by the overview rework go directly below this line)
  // -- design: entry (keys added by the entry rework go directly below this line)
  // -- design: planning (keys added by the planning rework go directly below this line)
  "budgets.add": "Set a budget",
  // -- design: admin (keys added by the admin rework go directly below this line)
  "login.example.salary": "Salary",
  "login.example.rent": "Rent",
  "login.example.groceries": "Groceries",
  "login.example.balance": "Balance",
  "data.dangerZoneTitle": "Delete",
  "data.dangerZoneHint":
    "Both steps are final. Download your data before you continue here.",
  "import.step1Title": "File & bank",

  // --------------------------------------------------------------- manual
  "manual.contents": "Contents",
  "manual.notFound": "This chapter does not exist",
  "manual.backToIndex": "Back to the overview",
  "manual.loadError": "The manual could not be loaded",
};
